import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Lock, LockOpen, FileCsv, FilePdf, ArrowRight, Archive, Package, CalendarBlank, X } from "@phosphor-icons/react";

const StatusBadge = ({ status }) =>
  status === "closed" ? (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-100 border border-emerald-200 rounded px-2 py-1">
      <Lock size={12} weight="fill" /> Closed
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-100 border border-amber-200 rounded px-2 py-1">
      <LockOpen size={12} weight="fill" /> Open
    </span>
  );

export default function Archives() {
  const [days, setDays] = useState(null);
  const [monthFilter, setMonthFilter] = useState("");

  useEffect(() => {
    api
      .getArchives()
      .then(setDays)
      .catch(() => toast.error("Failed to load archives"));
  }, []);

  const grouped = (days || []).reduce((acc, d) => {
    (acc[d.date] = acc[d.date] || []).push(d);
    return acc;
  }, {});
  const dates = Object.keys(grouped).sort().reverse();
  const visibleDates = monthFilter ? dates.filter((d) => d.startsWith(monthFilter)) : dates;
  const monthSections = visibleDates.reduce((acc, d) => {
    const m = d.slice(0, 7);
    (acc[m] = acc[m] || []).push(d);
    return acc;
  }, {});
  const monthKeys = Object.keys(monthSections).sort().reverse();
  const monthLabel = (m) => format(parseISO(`${m}-01`), "MMMM yyyy");

  return (
    <main data-testid="archives-page">
      <div
        className="relative bg-slate-900 bg-cover bg-center"
        style={{
          backgroundImage:
            "url(https://images.pexels.com/photos/257636/pexels-photo-257636.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
        }}
      >
        <div className="absolute inset-0 bg-slate-900/85" />
        <div className="relative max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white flex items-center gap-3">
            <Archive size={40} weight="duotone" className="text-orange-400" />
            Report Archives
          </h1>
          <p className="text-base md:text-lg text-slate-300 mt-2">
            Finalized daily KPI reports for the receiving team in Jamaica to double-check.
          </p>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {days !== null && dates.length > 0 && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6" data-testid="month-filter-bar">
            <label
              htmlFor="month-filter"
              className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500"
            >
              <CalendarBlank size={16} weight="duotone" /> Search by month
            </label>
            <input
              id="month-filter"
              data-testid="month-filter"
              type="month"
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              className="border border-slate-300 rounded-md bg-white px-3 py-2 text-sm font-semibold text-slate-700 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
            {monthFilter && (
              <button
                data-testid="month-filter-clear"
                onClick={() => setMonthFilter("")}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-700 bg-white border border-slate-300 rounded-md px-3 py-2 transition-colors duration-200"
              >
                <X size={14} weight="bold" /> Clear
              </button>
            )}
          </div>
        )}
        {days === null ? (
          <div className="text-center text-slate-500 py-16" data-testid="archives-loading">Loading archives...</div>
        ) : dates.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-12 text-center" data-testid="archives-empty">
            <Package size={48} weight="duotone" className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600 font-semibold">No reports yet</p>
            <p className="text-slate-500 text-sm mt-1">Log today's deliveries and close the day to create the first report.</p>
            <Link
              to="/"
              data-testid="archives-go-today"
              className="inline-flex items-center gap-2 mt-4 bg-orange-500 hover:bg-orange-600 text-white rounded-md font-semibold px-4 py-2 transition-colors duration-200"
            >
              Go to Today's Entry <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        ) : visibleDates.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-12 text-center" data-testid="archives-no-month-results">
            <p className="text-slate-600 font-semibold">No reports found for {monthLabel(monthFilter)}</p>
            <button
              data-testid="month-filter-show-all"
              onClick={() => setMonthFilter("")}
              className="inline-flex items-center gap-2 mt-4 bg-orange-500 hover:bg-orange-600 text-white rounded-md font-semibold px-4 py-2 transition-colors duration-200"
            >
              Show all reports
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            {monthKeys.map((m) => (
              <section key={m} data-testid={`archive-month-${m}`}>
                <div className="flex items-center justify-between border-b border-slate-200 pb-2 mb-4">
                  <h2
                    className="font-heading text-xl font-bold tracking-tight text-slate-900"
                    data-testid={`archive-month-label-${m}`}
                  >
                    {monthLabel(m)}
                  </h2>
                  <Link
                    to={`/report/month/${m}`}
                    data-testid={`monthly-report-btn-${m}`}
                    className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded-md font-semibold px-4 py-2 text-sm transition-colors duration-200"
                  >
                    View Monthly Report <ArrowRight size={14} weight="bold" />
                  </Link>
                </div>
                <div className="space-y-4">
                  {monthSections[m].map((date) => {
              const dayList = grouped[date].sort((a, b) => a.warehouse.localeCompare(b.warehouse));
              const totalPkgs = dayList.reduce((s, d) => s + d.total_packages, 0);
              const totalDels = dayList.reduce((s, d) => s + d.delivery_count, 0);
              return (
                <div
                  key={date}
                  className="bg-white border border-slate-200 rounded-md shadow-sm hover:border-slate-300 hover:shadow-md transition-all duration-200"
                  data-testid={`archive-date-${date}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4 p-5">
                    <div className="lg:w-56 flex-shrink-0">
                      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Report Date</div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-heading text-2xl font-bold text-slate-900 tracking-tight" data-testid={`archive-date-label-${date}`}>
                          {date}
                        </div>
                        {dayList.some((d) => d.demo) && (
                          <span
                            data-testid={`demo-badge-${date}`}
                            className="inline-flex items-center text-xs font-bold uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-300 rounded px-2 py-0.5"
                          >
                            Demo report
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-slate-500 mt-1" data-testid={`archive-totals-${date}`}>
                        {totalPkgs} packages · {totalDels} deliveries
                      </div>
                    </div>
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {dayList.map((d) => (
                        <div
                          key={d.id}
                          className="flex items-center justify-between border border-slate-200 rounded-md px-4 py-3 bg-slate-50"
                          data-testid={`archive-warehouse-${date}-${d.warehouse}`}
                        >
                          <div>
                            <div className="font-semibold text-slate-900">
                              {d.warehouse === "doral" ? "Doral, FL" : "Medley, FL"}
                            </div>
                            <div className="text-xs text-slate-500">
                              {d.total_packages} pkgs · {d.delivery_count}{" "}
                              {d.delivery_count === 1 ? "delivery" : "deliveries"}
                            </div>
                          </div>
                          <StatusBadge status={d.status} />
                        </div>
                      ))}
                    </div>
                    <div className="flex lg:flex-col gap-2 flex-shrink-0">
                      <Link
                        to={`/report/${date}`}
                        data-testid={`archive-view-${date}`}
                        className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white rounded-md font-semibold px-4 py-2 text-sm transition-colors duration-200"
                      >
                        View Report <ArrowRight size={14} weight="bold" />
                      </Link>
                      <div className="flex gap-2">
                        <a
                          href={api.csvUrl(date)}
                          data-testid={`archive-csv-${date}`}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-md font-semibold px-3 py-2 text-sm transition-colors duration-200"
                        >
                          <FileCsv size={16} weight="duotone" /> CSV
                        </a>
                        <a
                          href={api.pdfUrl(date)}
                          data-testid={`archive-pdf-${date}`}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-md font-semibold px-3 py-2 text-sm transition-colors duration-200"
                        >
                          <FilePdf size={16} weight="duotone" /> PDF
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
